import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { fetchToken } from "./fetchTokenService";
import { loginRequest } from "../auth/authConfig";
import { getConsumerKey } from "./maerskApiTokenService";

const API_BASE_URL = process.env.REACT_APP_FREE_TEXT_API_ENDPOINT || process.env.REACT_APP_POSTMORTEM_API_ENDPOINT;

// Helper function for retried axios requests
const axiosWithRetry = async (config, retries = 3, delay = 1000) => {
  try {
    return await axios.request(config);
  } catch (error) {
    const isRetryable = !error.response || (error.response?.status >= 500 && error.response?.status < 600) || !error.response;
    if (retries > 0 && isRetryable) {
      await new Promise((res) => setTimeout(res, delay));
      return axiosWithRetry(config, retries - 1, delay * 2);
    }
    throw error;
  }
};

export const FreeTextService = () => {
  const { instance, accounts } = useMsal();

  const getAuthHeaders = async () => {
    const tokenResponse = await fetchToken(instance, loginRequest, accounts);
    return {
      Authorization: `Bearer ${tokenResponse.idToken}`,
      "consumer-key": getConsumerKey(),
      "Content-Type": "application/json",
    };
  };

  const saveFreeTextField = async (postMortemId, incidentId, fieldName, freeText) => {
    try {
      const config = {
        method: "POST",
        url: `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/free-text`,
        headers: await getAuthHeaders(),
        data: { incidentId, fieldName, freeText },
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error saving free text field:", error);
      throw error;
    }
  };

  const saveFreeTextFields = async (postMortemId, incidentId, freeTextFields) => {
    try {
      const config = {
        method: "POST",
        url: `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/free-text/bulk`,
        headers: await getAuthHeaders(),
        data: { incidentId, freeTextFields },
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error saving free text fields:", error);
      throw error;
    }
  };

  const getFreeTextField = async (postMortemId, fieldName) => {
    try {
      const config = {
        method: "GET",
        url: `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/free-text/${fieldName}`,
        headers: await getAuthHeaders(),
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error fetching free text field:", error);
      throw error;
    }
  };

  const getAllFreeTextFields = async (postMortemId) => {
    try {
      const config = {
        method: "GET",
        url: `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/free-text`,
        headers: await getAuthHeaders(),
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error fetching all free text fields:", error);
      throw error;
    }
  };

  const acquireFreeTextLock = async (postMortemId, userEmail) => {
    try {
      const config = {
        method: "POST",
        url: `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/free-text/lock/acquire`,
        headers: await getAuthHeaders(),
        data: { userEmail },
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return error.response.data;
      }
      console.error("Error acquiring free text lock:", error);
      throw error;
    }
  };

  const releaseFreeTextLock = async (postMortemId, userEmail) => {
    try {
      const config = {
        method: "POST",
        url: `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/free-text/lock/release`,
        headers: await getAuthHeaders(),
        data: { userEmail },
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error releasing free text lock:", error);
      throw error;
    }
  };

  const checkFreeTextLock = async (postMortemId) => {
    try {
      const config = {
        method: "GET",
        url: `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/free-text/lock/status`,
        headers: await getAuthHeaders(),
      };
      const response = await axiosWithRetry(config, 3, 1000);
      return response.data;
    } catch (error) {
      console.error("Error checking free text lock:", error);
      throw error;
    }
  };

  const acquireEditLock = async (postMortemId, userEmail, userName, isNewTab = false, signal = undefined) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/edit/lock/acquire`,
        { userEmail, userName, isNewTab: String(isNewTab) },
        { headers: await getAuthHeaders(), ...(signal ? { signal } : {}) }
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      if (error.response && error.response.data) {
        return error.response.data;
      }
      console.error("Error acquiring edit lock:", error);
      throw error;
    }
  };

  const releaseEditLock = async (postMortemId, userEmail) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/edit/lock/release`,
        { userEmail },
        { headers: await getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error releasing edit lock:", error);
      throw error;
    }
  };

  const releaseEditLockBeacon = async (postMortemId, userEmail) => {
    try {
      const headers = await getAuthHeaders();
      await fetch(
        `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/edit/lock/release`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ userEmail }),
          keepalive: true,
        }
      );
    } catch (_) {
      // Intentionally swallowed — tab is closing and there's nothing to surface errors to
    }
  };

  const checkEditLock = async (postMortemId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/postmortem-enhancements/${postMortemId}/edit/lock/status`,
        { headers: await getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error checking edit lock:", error);
      throw error;
    }
  };

  return {
    saveFreeTextField,
    saveFreeTextFields,
    getFreeTextField,
    getAllFreeTextFields,
    acquireFreeTextLock,
    releaseFreeTextLock,
    checkFreeTextLock,
    acquireEditLock,
    releaseEditLock,
    releaseEditLockBeacon,
    checkEditLock,
  };
};
